import { getIsOnline } from "@client/hooks/useNetworkStatus";
import {
  clearCachedCurrentUser,
  isIPadCapacitor,
  isIPadOffline,
  isTransportError,
  OFFLINE_CACHE_KEYS,
  readCachedCurrentUser,
  readOfflineCache,
  writeCachedCurrentUser,
} from "@client/offline/offlineCache";
import { reconcileImpersonationOwner } from "@client/impersonationRef";
import { shouldRetryUserQuery } from "@client/offline/queryRetryPolicy";
import { trpc } from "@client/trpc";
import * as Sentry from "@client/telemetry/sentry";
import { identifyPostHogUser, resetPostHog } from "@client/telemetry/posthog";
import { useEffect, useRef } from "react";

// Track whether user was previously authenticated for analytics cleanup
let wasAuthenticated = false;

// Latched while the iPad fallback below is serving the cached identity
// because the backend is unreachable. React Query CLEARS `error` and re-enters
// `pending` when a dataless errored query refetches (query-core's `fetch`
// action), so a gate keyed on `query.error` alone flaps: serving the fallback
// mounts consumers whose very mount triggers such a refetch, which unmounts
// the whole app for the length of the retry cycle — a blank page oscillating
// every ~7s (SP-2612). The latch keeps the fallback serving through those
// pending phases until the server actually answers (success or a server-sent
// error), either of which clears it.
let ipadUnreachableIdentityLatched = false;

/** Test-only: clears the unreachable-identity latch between test cases. */
export function resetIPadUnreachableIdentityLatchForTests(): void {
  ipadUnreachableIdentityLatched = false;
}

/**
 * Hook to fetch the current user's data with automatic caching.
 * This is the single source of truth for user data on the client.
 * Multiple components using this hook will share the same cached data,
 * avoiding duplicate HTTP requests.
 *
 * First paint does not wait on the network (except on the iPad): while the
 * initial fetch is in flight, the identity cached in localStorage on the last
 * successful load is served, and the settling query then confirms or corrects
 * it. A settled UNAUTHORIZED clears that cache and yields no data, so the
 * login screen behaves exactly as before.
 *
 * The query is enabled by default - if the user is not authenticated,
 * the server will return UNAUTHORIZED and we handle that in AuthWrapper.
 * The iPhone shell passes `isOnline = false` while offline to disable the
 * query entirely (a fetch there is doomed anyway) and render from its cached
 * user instead — see useIPhoneCurrentUser. Note a disabled query never errors
 * and reports `isLoading` false.
 */
export function useCurrentUser(isOnline = true) {
  const query = trpc.getCurrentUser.useQuery(undefined, {
    enabled: isOnline,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 0, // Clear cache immediately on unmount (prevents stale user data on logout)
    // Don't retry auth failures (won't change) or while offline (fail fast →
    // cached-identity fallback in AuthWrapper); retry transient/server errors.
    // See queryRetryPolicy for the rationale + unit tests.
    retry: (failureCount, error) =>
      shouldRetryUserQuery({
        failureCount,
        isUnauthorized: error.data?.code === "UNAUTHORIZED",
        isOnline: getIsOnline(),
      }),
  });

  // Track previous data state to detect logout
  const hadDataRef = useRef(false);

  // Sentry/PostHog identification and cleanup
  useEffect(() => {
    if (query.data) {
      // Impersonation set by a different account must not survive into this
      // one (sessions can end without the sign-out page).
      reconcileImpersonationOwner(query.data.userId);
      // Mirror the authenticated user into the local identity cache (all
      // platforms) so the next page load renders immediately from it instead
      // of blocking on this query, and so the iPad app can open offline.
      writeCachedCurrentUser(query.data);
      // User is authenticated - identify them
      Sentry.setUser({
        id: query.data.userId,
        username: query.data.userName ?? undefined,
      });
      identifyPostHogUser(query.data.userId, {
        email: query.data.email,
        name: query.data.userName ?? undefined,
      });
      wasAuthenticated = true;
      hadDataRef.current = true;
    } else if (hadDataRef.current && (query.isError || !query.isLoading)) {
      // Transitioned from authenticated to unauthenticated - reset analytics
      if (wasAuthenticated) {
        Sentry.setUser(null);
        resetPostHog();
        wasAuthenticated = false;
      }
      hadDataRef.current = false;
    }
  }, [query.data, query.isError, query.isLoading]);

  // A settled UNAUTHORIZED means the cached identity is no longer good: clear
  // it so the next page load goes straight to the login screen instead of
  // flashing a shell rendered from the stale cache. NOT on the iPad — there
  // the entry doubles as the offline identity and must survive session expiry
  // so a previously-used round still opens offline (see TRPCProvider).
  const isUnauthorized = query.error?.data?.code === "UNAUTHORIZED";
  useEffect(() => {
    if (isUnauthorized && !isIPadCapacitor()) {
      clearCachedCurrentUser();
    }
  }, [isUnauthorized]);

  // On the iPad, when the query can't reach the server — offline, or the
  // backend is unreachable despite connectivity (SP-2612) — fall back to the
  // identity cached on the last online load. Doing it here — rather than only
  // in useEffectiveUser / AuthWrapper — means *every* consumer sees a stable
  // underlying user, including direct callers like QueueProvider's video-sync
  // outbox, which would otherwise key offline writes under "anonymous" and
  // abandon them on reconnect. Server-SENT errors (a real UNAUTHORIZED) never
  // take this branch. Inert on desktop/iPhone and whenever the query has data.
  //
  // Three signals decide "unreachable", because a refetch resets a dataless
  // errored query to pending with `error` cleared (see the latch above):
  // the settled `error`, the in-flight attempt's `failureReason` (populated
  // while retries are still running), and the latch bridging the gap between
  // a refetch starting and its first attempt failing — which against a
  // black-holing backend can be an entire fetch timeout.
  if (isIPadCapacitor() && !query.data) {
    const serverAnsweredWithError =
      query.error != null && !isTransportError(query.error) && !isIPadOffline();
    if (serverAnsweredWithError) {
      // The server actually answered (a real UNAUTHORIZED/FORBIDDEN) —
      // surface it, and stop bridging future pendings with the cached
      // identity. Offline takes priority: an error settled just as the
      // device went offline must not lock the funder out of cached work.
      ipadUnreachableIdentityLatched = false;
    } else {
      const attemptError = query.error ?? query.failureReason;
      if (
        isIPadOffline() ||
        (attemptError != null && isTransportError(attemptError)) ||
        (ipadUnreachableIdentityLatched && query.isLoading)
      ) {
        const cached = readOfflineCache<NonNullable<typeof query.data>>(
          OFFLINE_CACHE_KEYS.currentUser,
        );
        if (cached != null) {
          ipadUnreachableIdentityLatched = true;
          return { ...query, data: cached, isLoading: false, isError: false };
        }
      }
    }
  } else if (query.data != null) {
    // The server confirmed an identity — later pendings are ordinary
    // refetches again.
    ipadUnreachableIdentityLatched = false;
  }

  // Off the iPad, don't block first paint on the network: while the initial
  // fetch is in flight, render from the identity cached on the last
  // successful load. The in-flight query settles in the background and
  // confirms, replaces (different user), or ends it (UNAUTHORIZED → data
  // stays undefined → login, with the cache cleared by the effect above).
  // This applies only while `isLoading`, so a settled online error still
  // surfaces exactly as before — the cache never papers over a real failure.
  // The iPad is excluded deliberately: it keeps the dedicated offline flow
  // above, and serving an unconfirmed identity online would let
  // ArtifactCacheGate hydrate a persisted artifact snapshot for a user the
  // server hasn't confirmed (see TRPCProvider).
  if (!query.data && query.isLoading && !isIPadCapacitor()) {
    const cached = readCachedCurrentUser<NonNullable<typeof query.data>>();
    if (cached != null) {
      return { ...query, data: cached, isLoading: false, isError: false };
    }
  }

  return query;
}
